"use client";

import { useState } from "react";
import { Calculator, Zap, DollarSign, TrendingDown, Info, ArrowRight, Sun, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useStates } from "@/lib/useLocation";

export default function SolarCalculatorPage() {
  const { data: states, isLoading: statesLoading } = useStates("NG");

  const getSunHours = (stateName: string): number => {
    const n = stateName.toLowerCase();
    if (["kano", "katsina", "sokoto", "jigawa", "zamfara", "kebbi", "yobe", "borno"].some((s) => n.includes(s))) return 6.5;
    if (["kaduna", "niger", "plateau", "nassarawa", "nasarawa", "kwara", "benue", "federal capital", "abuja", "adamawa", "gombe", "taraba"].some((s) => n.includes(s))) return 6.0;
    if (["lagos", "ogun", "oyo", "osun", "ekiti", "ondo"].some((s) => n.includes(s))) return 5.5;
    return 5.0;
  };

  const [formData, setFormData] = useState({
    monthlyBill: "",
    roofSize: "",
    location: "",
    propertyType: "",
    dailyUsage: "",
    backupHours: ""
  });
  const [result, setResult] = useState<any>(null);
  const [errors, setErrors] = useState<any>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.monthlyBill || parseFloat(formData.monthlyBill) <= 0) {
      newErrors.monthlyBill = "Please enter a valid monthly bill amount";
    }

    if (!formData.roofSize || parseFloat(formData.roofSize) <= 0) {
      newErrors.roofSize = "Please enter a valid roof size";
    }

    if (!formData.location) {
      newErrors.location = "Please select your location";
    }

    if (!formData.propertyType) {
      newErrors.propertyType = "Please select property type";
    }

    if (!formData.dailyUsage || parseFloat(formData.dailyUsage) <= 0) {
      newErrors.dailyUsage = "Please enter your daily energy usage";
    }

    if (!formData.backupHours || parseFloat(formData.backupHours) <= 0) {
      newErrors.backupHours = "Please enter desired backup hours";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateSolar = () => {
    // Validate before calculating
    if (!validateForm()) {
      return;
    }

    const bill = parseFloat(formData.monthlyBill);
    const roof = parseFloat(formData.roofSize);
    const dailyUsage = parseFloat(formData.dailyUsage);
    const backupHours = parseFloat(formData.backupHours);

    const sunHours = getSunHours(formData.location);

    // More accurate calculations
    const systemSize = Math.min(
      roof * 0.15, // 150W per sqm
      (dailyUsage / sunHours) * 1.2 // 20% overhead
    );
    
    const panels = Math.ceil(systemSize / 0.4); // 400W panels
    const batteryCapacity = (dailyUsage * backupHours / 24) * 1.3; // 30% overhead
    
    // Cost calculations
    const panelCost = systemSize * 450000; // NGN per kW
    const batteryCost = batteryCapacity * 180000; // NGN per kWh
    const inverterCost = systemSize * 200000; // NGN per kW
    const installationCost = (panelCost + batteryCost + inverterCost) * 0.15;
    const totalCost = panelCost + batteryCost + inverterCost + installationCost;
    
    const annualSavings = bill * 12 * 0.90; // 90% savings
    const paybackPeriod = totalCost / annualSavings;
    const co2Offset = systemSize * 1.2 * 365; // kg per year

    setResult({
      systemSize: systemSize.toFixed(2),
      panels,
      batteryCapacity: batteryCapacity.toFixed(1),
      estimatedCost: totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      breakdown: {
        panels: panelCost.toLocaleString(undefined, { maximumFractionDigits: 0 }),
        battery: batteryCost.toLocaleString(undefined, { maximumFractionDigits: 0 }),
        inverter: inverterCost.toLocaleString(undefined, { maximumFractionDigits: 0 }),
        installation: installationCost.toLocaleString(undefined, { maximumFractionDigits: 0 })
      },
      annualSavings: annualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      monthlySavings: (annualSavings / 12).toLocaleString(undefined, { maximumFractionDigits: 0 }),
      paybackPeriod: paybackPeriod.toFixed(1),
      co2Offset: co2Offset.toFixed(0),
      sunHours: sunHours.toFixed(1)
    });

    // Scroll to results
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all ${
      errors[field]
        ? "border-red-400 focus:ring-red-300"
        : "border-gray-200 focus:border-[#17BC2E] focus:ring-[#17BC2E]/20"
    }`;

  return (
    <div className="min-h-screen bg-[#f4f6f6]">

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="bg-[#002301] pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="calc-dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#calc-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#FFB600]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
              <Calculator className="w-3.5 h-3.5 text-[#FFB600]" />
              <span className="text-white/80 text-xs font-medium">Free Tool</span>
            </span>
            <h1 className="text-white font-bold text-4xl sm:text-5xl leading-tight mb-4">
              Calculate Your <span className="text-[#FFB600]">Solar Savings</span>
            </h1>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              Get an instant estimate of system size, total cost, and payback period
              based on your actual energy usage and location in Nigeria.
            </p>
            <div className="flex flex-wrap gap-6">
              {[
                { icon: Sun, label: "Instant estimate" },
                { icon: DollarSign, label: "NGN cost breakdown" },
                { icon: Leaf, label: "CO₂ impact" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-white/70 text-sm">
                  <Icon className="w-4 h-4 text-[#17BC2E]" />
                  {label}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]"
          >
            <img
              src="/assets/img/solar-calculator-hero.png"
              alt="Solar Calculator"
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).src = "/assets/img/system-for-remote-workers.png"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#002301]/30 to-transparent" />
          </motion.div>
        </div>
      </div>

      {/* ── Calculator Form ───────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
        >
          <h2 className="text-[#002301] text-2xl font-bold mb-2">Your Energy Details</h2>
          <p className="text-gray-500 text-sm mb-8">All fields are required for an accurate estimate.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Monthly Electricity Bill (NGN) *
              </label>
              <input type="number" value={formData.monthlyBill}
                onChange={(e) => handleInputChange("monthlyBill", e.target.value)}
                placeholder="e.g. 50,000" className={inputClass("monthlyBill")} />
              {errors.monthlyBill && <p className="text-red-500 text-xs mt-1">{errors.monthlyBill}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Available Roof Space (sqm) *
              </label>
              <input type="number" value={formData.roofSize}
                onChange={(e) => handleInputChange("roofSize", e.target.value)}
                placeholder="e.g. 50" className={inputClass("roofSize")} />
              {errors.roofSize && <p className="text-red-500 text-xs mt-1">{errors.roofSize}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Location (State) *</label>
              <select value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                disabled={statesLoading}
                className={inputClass("location")}>
                <option value="">{statesLoading ? "Loading states..." : "Select your state"}</option>
                {states?.map((s) => (
                  <option key={s.iso2} value={s.name}>{s.name}</option>
                ))}
              </select>
              {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Property Type *</label>
              <select value={formData.propertyType}
                onChange={(e) => handleInputChange("propertyType", e.target.value)}
                className={inputClass("propertyType")}>
                <option value="">Select property type</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Industrial">Industrial</option>
              </select>
              {errors.propertyType && <p className="text-red-500 text-xs mt-1">{errors.propertyType}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Daily Energy Usage (kWh) *
              </label>
              <input type="number" value={formData.dailyUsage}
                onChange={(e) => handleInputChange("dailyUsage", e.target.value)}
                placeholder="e.g. 15" className={inputClass("dailyUsage")} />
              {errors.dailyUsage && <p className="text-red-500 text-xs mt-1">{errors.dailyUsage}</p>}
              <p className="text-xs text-gray-400 mt-1">Check your electricity meter or bill</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Desired Backup Hours *
              </label>
              <input type="number" value={formData.backupHours}
                onChange={(e) => handleInputChange("backupHours", e.target.value)}
                placeholder="e.g. 12" className={inputClass("backupHours")} />
              {errors.backupHours && <p className="text-red-500 text-xs mt-1">{errors.backupHours}</p>}
              <p className="text-xs text-gray-400 mt-1">How many hours of battery backup do you need?</p>
            </div>
          </div>

          <button onClick={calculateSolar}
            className="w-full bg-[#FFB600] text-[#002301] py-4 rounded-xl font-bold text-base hover:bg-[#17BC2E] hover:text-white transition-all duration-300 flex items-center justify-center gap-2">
            <Calculator className="w-5 h-5" />
            Calculate My Solar System
          </button>

          {/* ── Disclaimer ── */}
          <div className="mt-6 bg-[#f4f6f6] rounded-xl p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-[#17BC2E] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              <strong className="text-gray-700">Estimates only.</strong> Results are based on average Nigerian values.
              For accurate quotes, contact verified providers on our marketplace.
            </p>
          </div>
        </motion.div>

        {/* ── Results ───────────────────────────────────────── */}
        {result && (
          <motion.div
            id="results"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 space-y-6"
          >
            {/* 3 stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Zap, label: "System Size", value: `${result.systemSize} kW`, sub: `${result.panels} panels · ${result.batteryCapacity} kWh battery`, color: "text-[#17BC2E]" },
                { icon: DollarSign, label: "Total Investment", value: `₦${result.estimatedCost}`, sub: "Installation included", color: "text-[#FFB600]" },
                { icon: TrendingDown, label: "Annual Savings", value: `₦${result.annualSavings}`, sub: `₦${result.monthlySavings}/month`, color: "text-blue-500" },
              ].map(({ icon: Icon, label, value, sub, color }) => (
                <div key={label} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <Icon className={`w-8 h-8 mb-3 ${color}`} />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                  <p className={`text-3xl font-bold ${color} mb-1`}>{value}</p>
                  <p className="text-gray-400 text-xs">{sub}</p>
                </div>
              ))}
            </div>

            {/* Cost breakdown */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-[#002301] text-lg font-bold mb-5">Cost Breakdown</h3>
              <div className="space-y-3">
                {[
                  { label: "Solar Panels", value: result.breakdown.panels },
                  { label: "Battery Storage", value: result.breakdown.battery },
                  { label: "Inverter System", value: result.breakdown.inverter },
                  { label: "Installation & Setup", value: result.breakdown.installation },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className="text-sm font-semibold text-[#002301]">₦{value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-3">
                  <span className="font-bold text-[#002301]">Total Investment</span>
                  <span className="font-bold text-xl text-[#FFB600]">₦{result.estimatedCost}</span>
                </div>
              </div>
            </div>

            {/* ROI + environment */}
            <div className="bg-[#002301] rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-[#FFB600] text-xs font-semibold uppercase tracking-widest mb-3">Return on Investment</p>
                  <p className="text-5xl font-bold mb-1">{result.paybackPeriod}<span className="text-2xl font-normal text-white/60 ml-1">yrs</span></p>
                  <p className="text-white/50 text-sm mb-5">Payback period</p>
                  <p className="text-white/70 text-sm leading-relaxed">
                    After payback, enjoy free solar energy for 20+ years, every naira saved goes straight back to you.
                  </p>
                </div>
                <div>
                  <p className="text-[#FFB600] text-xs font-semibold uppercase tracking-widest mb-3">Environmental Impact</p>
                  <div className="space-y-4">
                    <div>
                      <p className="text-white/50 text-xs">CO₂ Offset per year</p>
                      <p className="text-3xl font-bold">{result.co2Offset} <span className="text-base font-normal text-white/50">kg</span></p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Daily sun hours · {formData.location}</p>
                      <p className="text-3xl font-bold">{result.sunHours} <span className="text-base font-normal text-white/50">hrs</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#FFB600] rounded-2xl p-8 text-center">
              <h3 className="text-[#002301] text-2xl font-bold mb-2">Ready to Go Solar?</h3>
              <p className="text-[#002301]/70 text-sm mb-6">
                Connect with verified Nigerian solar providers and get exact quotes based on your estimate.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link href="/marketplace"
                  className="inline-flex items-center justify-center gap-2 bg-[#002301] text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-black transition-colors shadow-lg">
                  Browse Solar Providers <ArrowRight className="w-4 h-4" />
                </Link>
                <Link href="/auth/register"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#002301] px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-gray-50 transition-colors shadow-lg">
                  Create Free Account
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
